/** Which notifications interrupt, and which merely arrive.
 *
 *  Everything concerning a person lands in their list either way. This file is only
 *  about what pops up on top of whatever they are doing — and the rule the Owner gave
 *  is *"if it is for the person or important"*, so being addressed to you is one axis
 *  and urgency is a second, not a narrowing of the first.
 *
 *  Pure, and separate from the component, because `vitest` runs `environment: 'node'`
 *  and cannot render a toast — but these rules are the part worth pinning. Same split
 *  as `NewTasks.grid.ts` and `TaskDrawer.thread.ts`.
 */

/** The shape this needs, so the module does not depend on the generated API type for
 *  what is really a decision about wording and attention. */
export type Notice = {
  notice_id: string
  category: string
  kind: string
  summary: string
  detail?: string
  ref?: number
  link_path?: string
  created_at: string
  resolved?: boolean
  /** 'open' until the person has read it. Never 'deleted': reading is not removing,
   *  and the row stays in the list. */
  state?: string
}

/** How loudly, if at all.
 *
 *  `sticky` stays until it is dismissed: somebody else is blocked until this person
 *  acts, and a toast that fades after eight seconds is indistinguishable from never
 *  having appeared. `brief` auto-dismisses. `quiet` never pops. */
export type Volume = 'sticky' | 'brief' | 'quiet'

/** Somebody is waiting on this person to do something. */
const WAITING_ON_ME = new Set([
  'approval_waiting',     // a decision sitting on their desk
  'task_assigned',        // work handed to them
  'query_raised',         // a question put to them - not emitted yet
])

/** The answer to something they did. */
const ABOUT_MY_WORK = new Set([
  'approved', 'rework', 'rejected',
  'query_answered',       // not emitted yet
])

export function volumeOf(n: Notice): Volume {
  // Already dealt with by somebody else - an approval since decided. It stays in the
  // list because it did happen; interrupting about it would be a lie.
  if (n.resolved) return 'quiet'
  if (WAITING_ON_ME.has(n.kind)) return 'sticky'
  if (ABOUT_MY_WORK.has(n.kind)) return 'brief'
  if (n.category === 'work') return 'brief'
  // Day sign-offs, and anything a later release adds without thinking about this
  // file. Arriving in the list is the safe default; interrupting is not.
  return 'quiet'
}

/** An `Urgent` task raises whatever it is to the sticky tier.
 *
 *  The second axis in "for the person **or** important". Priority is not on the notice
 *  itself — it belongs to the task — so the caller passes the refs it knows to be
 *  urgent, which the list on screen already has. */
export function withUrgency(v: Volume, urgent: boolean): Volume {
  if (v === 'quiet') return 'quiet'       // urgency does not make noise out of nothing
  return urgent ? 'sticky' : v
}

export type Popped =
  | { kind: 'one'; notice: Notice; volume: Volume }
  | { kind: 'several'; notices: Notice[]; volume: Volume }

/** What to actually show, given everything that has arrived since the last check.
 *
 *  Three rules, and each of them is the difference between useful and hated:
 *
 *  * **Only what is new.** `seen` is every id already dealt with, including the
 *    backlog recorded when the session started — otherwise somebody back from leave
 *    opens the app and is buried in forty toasts about last week.
 *  * **Never twice.** Ids, not positions or timestamps, so a reload cannot replay.
 *  * **Coalesce the chatty kind.** An Owner working down a list changes eight tasks in
 *    a minute; eight toasts about it is the thing that makes people turn the feature
 *    off. Anything that would show more than one `brief` becomes a single summary.
 *    Sticky ones are never coalesced away — each needs its own action.
 */
export function toPop(arrived: Notice[], seen: Set<string>,
                      urgentRefs: Set<number> = new Set()): Popped[] {
  const fresh = arrived.filter((n) => !seen.has(n.notice_id))
  const rated = fresh
    .map((n) => ({
      notice: n,
      volume: withUrgency(volumeOf(n), !!n.ref && urgentRefs.has(n.ref)),
    }))
    .filter((r) => r.volume !== 'quiet')

  const sticky = rated.filter((r) => r.volume === 'sticky')
  const brief = rated.filter((r) => r.volume === 'brief')

  const out: Popped[] = sticky.map(
    (r) => ({ kind: 'one', notice: r.notice, volume: 'sticky' } as Popped))
  if (brief.length === 1) {
    out.push({ kind: 'one', notice: brief[0].notice, volume: 'brief' })
  } else if (brief.length > 1) {
    out.push({ kind: 'several', notices: brief.map((r) => r.notice),
               volume: 'brief' })
  }
  return out
}

/** What a coalesced toast says. Counted, not listed: five summaries stacked in one
 *  toast is the same wall of text the coalescing was meant to avoid. */
export const severalLabel = (notices: Notice[]): string =>
  `${notices.length} updates on your tasks`

/** Every id in a batch, for adding to `seen` once shown — including the quiet ones,
 *  which have been dealt with by arriving. */
export const idsIn = (arrived: Notice[]): string[] =>
  arrived.map((n) => n.notice_id)
